/**
 * ORW Cart Drawer
 *
 * Opens on bag icon click, renders items, supports qty change and remove.
 *
 * Speed: the cart is seeded from Liquid ({{ cart | json }}) so the drawer
 * opens with content instantly, and every add / change is ONE Ajax call:
 *   - window.ORWCart.add(items) posts /cart/add.js and merges the returned
 *     lines into the cached cart (no follow-up /cart.js wait)
 *   - qty / remove update the UI first, then post /cart/change.js (which
 *     returns the full cart). Calls are queued so fast taps apply in order.
 * A background /cart.js refresh after each add picks up discounts.
 */

class ORWCartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-orw-cart-drawer]');
    if (!this.drawer) return;

    // Query inside the drawer only: the cart page has its own list, title
    // and totals, and a page-wide querySelector would pick those up first.
    var d = this.drawer;
    this.overlay = d.querySelector('[data-orw-cart-overlay]');
    this.closeBtn = d.querySelector('[data-orw-cart-close]');
    this.titleEl = d.querySelector('[data-orw-cart-title]');
    this.itemsEl = d.querySelector('[data-orw-drawer-items]');
    this.totalEl = d.querySelector('[data-orw-cart-total]');
    this.clearBtn = d.querySelector('[data-orw-cart-clear]');
    this.checkoutBtn = d.querySelector('[data-orw-cart-checkout]');
    this.savingsEl = d.querySelector('[data-orw-cart-savings]');

    this.cart = null;
    this.pending = 0;          // in-flight add / change calls
    this.version = 0;          // bumps on every local change; stale responses are dropped
    this.queue = Promise.resolve();

    var seed = document.querySelector('[data-orw-cart-json]');
    if (seed) {
      try { this.cart = JSON.parse(seed.textContent); } catch (e) { this.cart = null; }
    }

    // Compare-at price per variant id (0 = none). /cart.js has no compare-at,
    // so it is seeded from Liquid and topped up from /products/{handle}.js
    // for lines added later. Shared with the cart page via window.ORWCart.
    this.compareAt = {};
    var capSeed = document.querySelector('[data-orw-compare-at-json]');
    if (capSeed) {
      try { this.compareAt = JSON.parse(capSeed.textContent) || {}; } catch (e) { this.compareAt = {}; }
    }

    // Free gift line (orw-free-gift.js adds / removes it): shown as a label, no qty or delete
    this.giftVariantId = 0;
    this.giftLabel = 'Free gift';
    var giftCfg = document.querySelector('[data-orw-free-gift]');
    if (giftCfg) {
      try {
        var g = JSON.parse(giftCfg.textContent);
        this.giftVariantId = g.variantId || 0;
        this.giftLabel = g.label || this.giftLabel;
      } catch (e) { /* no gift */ }
    }
    // The gift has no compare-at to show: never read its product for one
    if (this.giftVariantId) this.compareAt[this.giftVariantId] = 0;
    this.capRequests = {};     // handle -> pending /products/{handle}.js read

    this.bindEvents();
    this.exposeApi();
  }

  // Keep the cart page (and anything else) in step with the drawer
  announce(cart) {
    document.dispatchEvent(new CustomEvent('orw:cart:changed', { detail: { source: 'drawer', cart: cart || null } }));
  }

  exposeApi() {
    var self = this;
    window.ORWCart = {
      add: function(items) { return self.add(items); },
      refresh: function() { self.fetchCart(); },
      compareMap: this.compareAt,
      loadCompareAt: function(cart) { return self.loadCompareAt(cart); },
      savings: function(cart) { return self.savings(cart); }
    };
  }

  bindEvents() {
    var self = this;

    // Open on bag icon click
    document.addEventListener('click', function(e) {
      var bagBtn = e.target.closest('[data-orw-cart-open]');
      if (bagBtn) {
        e.preventDefault();
        e.stopPropagation();
        self.open();
      }
    });

    // Close
    if (this.closeBtn) this.closeBtn.addEventListener('click', function() { self.close(); });
    if (this.overlay) this.overlay.addEventListener('click', function() { self.close(); });

    // Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });

    // Listen for external open event (from Add to Cart)
    document.addEventListener('orw:cart:open', function() {
      self.open();
    });

    // Listen for external refresh event (e.g. card-actions.js dispatches
    // this after /cart/add.js resolves so the drawer re-fetches and shows
    // the just-added item without the user waiting for the network
    // round-trip before the drawer opens).
    // The cart page changed the cart: take its (true /cart.js) cart
    document.addEventListener('orw:cart:changed', function(e) {
      var detail = e.detail || {};
      if (detail.source === 'drawer') return;
      if (detail.cart && self.pending === 0) {
        self.version++;
        self.render(detail.cart);
      }
    });

    document.addEventListener('orw:cart:refresh', function() {
      if (self.isOpen()) self.fetchCart();
    });

    // Clear cart
    if (this.clearBtn) this.clearBtn.addEventListener('click', function() { self.clearCart(); });

    // Delegated clicks for qty and delete
    if (this.itemsEl) {
      this.itemsEl.addEventListener('click', function(e) {
        var minus = e.target.closest('[data-cart-qty-minus]');
        var plus = e.target.closest('[data-cart-qty-plus]');
        var del = e.target.closest('[data-cart-delete]');

        if (minus) self.updateQty(minus.dataset.cartQtyMinus, -1);
        if (plus) self.updateQty(plus.dataset.cartQtyPlus, 1);
        if (del) self.removeItem(del.dataset.cartDelete);
      });
    }
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('orw-cart-drawer--open');
  }

  open(skipFetch) {
    this.drawer.classList.add('orw-cart-drawer--open');
    if (this.cart) this.render(this.cart);
    if (!skipFetch) this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('orw-cart-drawer--open');
  }

  fetchCart() {
    var self = this;
    var v = this.version;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Ignore if the user changed the cart while this was in flight
        if (v === self.version && self.pending === 0) self.render(cart);
        return cart;
      })
      .catch(function(err) { console.error('Cart fetch error:', err); });
  }

  /* ---------- Compare-at savings ---------- */

  // Fetches compare-at prices for lines we don't know yet. Resolves true
  // when something new arrived (caller re-renders), false otherwise.
  loadCompareAt(cart) {
    var self = this;
    var handles = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (!(items[i].variant_id in this.compareAt) && items[i].handle) handles[items[i].handle] = true;
    }
    var list = Object.keys(handles);
    if (!list.length) return Promise.resolve(false);

    // One read per handle: the drawer, the cart page and the gift script can
    // all ask at once, and they share the same pending request
    return Promise.all(list.map(function(handle) {
      if (self.capRequests[handle]) return self.capRequests[handle];
      var req = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(product) {
          if (!product) return;
          for (var j = 0; j < product.variants.length; j++) {
            self.compareAt[product.variants[j].id] = product.variants[j].compare_at_price || 0;
          }
        })
        .catch(function() {})
        .then(function() { delete self.capRequests[handle]; });
      self.capRequests[handle] = req;
      return req;
    })).then(function() {
      // Never ask twice for a line whose product could not be read
      for (var k = 0; k < items.length; k++) {
        if (!(items[k].variant_id in self.compareAt)) self.compareAt[items[k].variant_id] = 0;
      }
      return true;
    });
  }

  // Struck-through "was" amount for a line: the compare-at total, or the
  // pre-discount line price when only a code discounted it. 0 = none.
  wasPrice(item) {
    var cap = (this.compareAt[item.variant_id] || 0) * item.quantity;
    var was = Math.max(cap, item.original_line_price || 0);
    return was > item.final_line_price ? was : 0;
  }

  // Compare-at savings plus every discount (codes and automatic)
  savings(cart) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var cap = this.compareAt[items[i].variant_id] || 0;
      if (cap > items[i].original_price) total += (cap - items[i].original_price) * items[i].quantity;
    }
    return total + ((cart && cart.total_discount) || 0);
  }

  renderSavings(cart) {
    if (!this.savingsEl) return;
    var amount = cart._totalPending ? 0 : this.savings(cart);
    this.savingsEl.hidden = amount <= 0;
    if (amount > 0) this.savingsEl.textContent = 'You save ' + this.formatMoney(amount);
  }

  /* ---------- Add (used by every Add to cart button) ---------- */

  add(items) {
    var self = this;
    // Free gift rides in the same request, so it shows with the watch
    if (window.ORWFreeGift && window.ORWFreeGift.withGift) items = window.ORWFreeGift.withGift(items);
    this.version++;
    this.setBusy(1);
    this.clearError();
    this.open(true);
    this.showAdding();

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(r) {
      return r.json().then(function(data) {
        if (!r.ok) throw data;
        return data;
      });
    })
    .then(function(data) {
      self.setBusy(-1);
      if (self.cart) {
        self.mergeLines(data.items || [data]);
        self.render(self.cart);
      }
      // Background /cart.js: discounts and exact totals; share it with the cart page
      self.fetchCart().then(function(cart) { self.announce(cart); });
      return data;
    })
    .catch(function(err) {
      self.setBusy(-1);
      self.hideAdding();
      self.showError((err && (err.description || err.message)) || 'Could not add to cart. Please try again.');
      self.fetchCart();
      throw err;
    });
  }

  // Placeholder row while /cart/add.js is on its way (render() replaces it)
  showAdding() {
    if (!this.itemsEl) return;
    var empty = this.itemsEl.querySelector('.orw-cart-drawer__empty');
    if (empty) empty.remove();
    if (this.itemsEl.querySelector('.orw-cart-drawer__adding')) return;
    var row = document.createElement('div');
    row.className = 'orw-cart-drawer__adding';
    row.setAttribute('role', 'status');
    row.innerHTML = '<span class="orw-cart-drawer__adding-spinner" aria-hidden="true"></span><span>Adding to your bag\u2026</span>';
    this.itemsEl.insertBefore(row, this.itemsEl.firstChild);
  }

  hideAdding() {
    var row = this.itemsEl && this.itemsEl.querySelector('.orw-cart-drawer__adding');
    if (row) row.remove();
    if (this.cart) this.render(this.cart);
  }

  mergeLines(lines) {
    var items = this.cart.items;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var found = false;
      for (var j = 0; j < items.length; j++) {
        if (items[j].key === line.key) { items[j] = line; found = true; break; }
      }
      if (!found) items.unshift(line); // Shopify lists newest line first
    }
    this.recalc();
  }

  recalc() {
    var count = 0, total = 0;
    for (var i = 0; i < this.cart.items.length; i++) {
      count += this.cart.items[i].quantity;
      total += this.cart.items[i].final_line_price;
    }
    this.cart.item_count = count;
    // An order-level code (e.g. 10% off) changes with the items, so the true
    // total only comes from /cart.js. Keep the last one and mark it pending
    // rather than flash an undiscounted number.
    var orderLevel = this.cart.cart_level_discount_applications || [];
    if (orderLevel.length) {
      this.cart._totalPending = true;
    } else {
      this.cart.total_price = total;
      this.cart._totalPending = false;
    }
  }

  findItem(key) {
    if (!this.cart) return null;
    for (var i = 0; i < this.cart.items.length; i++) {
      if (this.cart.items[i].key === key) return this.cart.items[i];
    }
    return null;
  }

  setBusy(delta) {
    this.pending = Math.max(0, this.pending + delta);
    this.drawer.classList.toggle('orw-cart-drawer--busy', this.pending > 0);
    this.syncCheckout();
  }

  syncCheckout() {
    if (!this.checkoutBtn) return;
    var empty = !this.cart || this.cart.item_count === 0;
    this.checkoutBtn.disabled = empty || this.pending > 0;
  }

  showError(msg) {
    if (!this.itemsEl) return;
    this.clearError();
    var el = document.createElement('p');
    el.className = 'orw-cart-drawer__error';
    el.setAttribute('role', 'alert');
    el.textContent = msg;
    this.itemsEl.parentNode.insertBefore(el, this.itemsEl);
    this.errorTimer = setTimeout(this.clearError.bind(this), 5000);
  }

  clearError() {
    clearTimeout(this.errorTimer);
    var el = this.drawer.querySelector('.orw-cart-drawer__error');
    if (el) el.remove();
  }

  render(cart) {
    this.cart = cart;

    // Update title
    if (this.titleEl) {
      this.titleEl.textContent = 'Your Bag (' + cart.item_count + ')';
    }

    // Update total (faded while an order-level discount is being recalculated)
    if (this.totalEl) {
      this.totalEl.classList.toggle('orw-cart-drawer__subtotal-price--pending', !!cart._totalPending);
      if (!cart._totalPending) this.totalEl.textContent = this.formatMoney(cart.total_price);
    }

    // Update header cart count
    var countEl = document.querySelector('[data-orw-cart-count]');
    if (countEl) countEl.textContent = cart.item_count || '';

    // Checkout needs at least one item and no change in flight
    this.syncCheckout();

    this.renderSavings(cart);

    // Lines added since page load: fetch their compare-at, then draw again
    var self = this;
    this.loadCompareAt(cart).then(function(changed) {
      if (changed && self.cart === cart) self.render(cart);
    });

    // Render items
    if (!this.itemsEl) return;

    if (cart.items.length === 0) {
      this.itemsEl.innerHTML = '<div class="orw-cart-drawer__empty">Your bag is empty</div>';
      return;
    }

    var html = '';
    var rows = this.groupLines(cart.items);
    for (var i = 0; i < rows.length; i++) {
      html += this.renderItem(rows[i]);
    }
    this.itemsEl.innerHTML = html;
  }

  renderItem(item) {
    var imageUrl = item.featured_image && item.featured_image.url ? item.featured_image.url.replace(/(\.[^.]+)$/, '_320x$1') : '';
    var brand = item.vendor || '';
    var qty = item.quantity;
    var qtyDisplay = qty < 10 ? '0' + qty : String(qty);
    var minusOpacity = qty <= 1 ? ' opacity="0.5"' : '';
    var isGift = this.giftVariantId && item.variant_id === this.giftVariantId;

    return '' +
      '<div class="orw-cart-drawer__item">' +
        '<div class="orw-cart-drawer__item-image">' +
          (imageUrl ? '<img src="' + imageUrl + '" alt="' + this.esc(item.title) + '" class="orw-cart-drawer__item-img">' : '') +
        '</div>' +
        '<div class="orw-cart-drawer__item-info">' +
          '<div class="orw-cart-drawer__item-details">' +
            '<p class="orw-cart-drawer__item-title">' + this.titleHtml(item.title) + '</p>' +
            (brand ? '<span class="orw-cart-drawer__item-brand">' + this.esc(brand) + '</span>' : '') +
          '</div>' +
          '<span class="orw-cart-drawer__item-price">' +
            (this.wasPrice(item) ? '<s class="orw-cart-drawer__item-price-was">' + this.formatMoney(this.wasPrice(item)) + '</s>' : '') +
            this.formatMoney(item.final_line_price) +
          '</span>' +
          (isGift ? '<span class="orw-cart-drawer__gift-tag">' + this.esc(this.giftLabel) + (qty > 1 ? ' &times; ' + qty : '') + '</span>' :
          '<div class="orw-cart-drawer__item-actions">' +
            '<div class="orw-cart-drawer__qty-stepper">' +
              '<button type="button" class="orw-cart-drawer__qty-btn" data-cart-qty-minus="' + item.key + '" aria-label="Decrease">' +
                '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><line x1="6" y1="10" x2="14" y2="10" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"' + minusOpacity + '/></svg>' +
              '</button>' +
              '<span class="orw-cart-drawer__qty-val">' + qtyDisplay + '</span>' +
              '<button type="button" class="orw-cart-drawer__qty-btn" data-cart-qty-plus="' + item.key + '" aria-label="Increase">' +
                '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><line x1="6" y1="10" x2="14" y2="10" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="6" x2="10" y2="14" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/></svg>' +
              '</button>' +
            '</div>' +
            '<button type="button" class="orw-cart-drawer__delete" data-cart-delete="' + item.key + '" aria-label="Remove">' +
              '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                '<path d="M12.4692 3.23248L13.151 4.71981H15.3505C16.0267 4.71981 16.575 5.26805 16.575 5.94433V6.85117C16.575 7.31354 16.2002 7.68838 15.7378 7.68838H4.263C3.80061 7.68838 3.42578 7.31354 3.42578 6.85117V5.94433C3.42578 5.26805 3.97401 4.71981 4.65031 4.71981H6.84982L7.53166 3.23248C7.73627 2.78615 8.18224 2.5 8.67324 2.5H11.3276C11.8186 2.5 12.2645 2.78615 12.4692 3.23248Z" stroke="#7C7C7C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '<path d="M15.2923 7.75064V14.9887C15.2923 16.3759 14.1817 17.5004 12.8118 17.5004H7.18946C5.81953 17.5004 4.70898 16.3759 4.70898 14.9887V7.75064" stroke="#7C7C7C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</button>' +
          '</div>') +
        '</div>' +
      '</div>';
  }

  /* Shopify splits a line when a discount covers only part of it (the free
     gift's "buy" unit), so one watch can come back as 2 lines. Show one row
     per variant + properties; its key is the first line's, keys lists all. */
  groupLines(items) {
    var rows = [], byId = {};
    for (var i = 0; i < (items || []).length; i++) {
      var it = items[i];
      var id = it.variant_id + '|' + JSON.stringify(it.properties || {});
      var row = byId[id];
      if (!row) {
        row = byId[id] = Object.assign({}, it, { keys: [it.key] });
        rows.push(row);
      } else {
        row.quantity += it.quantity;
        row.final_line_price += it.final_line_price;
        row.original_line_price += it.original_line_price;
        row.keys.push(it.key);
      }
    }
    return rows;
  }

  findRow(key) {
    var rows = this.groupLines(this.cart && this.cart.items);
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].keys.indexOf(key) !== -1) return rows[i];
    }
    return null;
  }

  updateQty(key, delta) {
    var row = this.findRow(key);
    if (!row) return;
    this.changeLine(key, Math.max(0, row.quantity + delta));
  }

  removeItem(key) {
    this.changeLine(key, 0);
  }

  changeLine(key, qty) {
    var self = this;
    var item = this.findItem(key);
    if (!item) return;

    // A split row: the first line takes the qty, the others go. The free gift
    // count follows in the same request (orw-free-gift.js plan).
    var row = this.findRow(key);
    var keys = row ? row.keys : [key];
    var updates = {};
    for (var k = 0; k < keys.length; k++) updates[keys[k]] = k === 0 ? qty : 0;
    if (window.ORWFreeGift && window.ORWFreeGift.plan) updates = window.ORWFreeGift.plan(this.cart, updates);
    var changed = Object.keys(updates);
    var request = changed.length === 1
      ? { url: '/cart/change.js', body: { id: changed[0], quantity: updates[changed[0]] } }
      : { url: '/cart/update.js', body: { updates: updates } };

    // Show the result straight away
    for (var c = 0; c < changed.length; c++) {
      var line = this.findItem(changed[c]);
      if (!line) continue;
      var n = updates[changed[c]];
      if (n === 0) {
        this.cart.items.splice(this.cart.items.indexOf(line), 1);
      } else {
        line.quantity = n;
        line.final_line_price = line.final_price * n;
        line.original_line_price = line.original_price * n;
      }
    }
    this.recalc();
    this.render(this.cart);
    this.clearError();

    var v = ++this.version;
    this.setBusy(1);

    // Queue so quick taps reach Shopify in order (each sends the absolute qty)
    this.queue = this.queue.then(function() {
      return fetch(request.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(request.body)
      })
      .then(function(r) {
        return r.json().then(function(data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function() {
        self.setBusy(-1);
        // Only the latest change refreshes. Totals come from /cart.js: after
        // a checkout picked a shipping method, change.js reports total_price
        // with shipping included (the UI already shows the new qty).
        if (v === self.version) {
          self.fetchCart().then(function(cart) { self.announce(cart); });
        }
      })
      .catch(function(err) {
        self.setBusy(-1);
        self.showError((err && (err.description || err.message)) || 'Could not update your bag. Please try again.');
        self.version++;
        self.fetchCart();
      });
    });
  }

  clearCart() {
    var self = this;
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(r) { return r.json(); })
    .then(function(cart) { self.render(cart); })
    .catch(function(err) { console.error('Cart clear error:', err); });
  }

  formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var rupees = Math.floor(cents / 100);
    return '\u20B9' + rupees.toLocaleString('en-IN');
  }

  // Hyphenated words (model numbers) never split across lines
  titleHtml(title) {
    return this.esc(title).replace(/(\S+-\S+)/g, '<span class="orw-cart-drawer__nowrap">$1</span>');
  }

  esc(text) {
    var d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() { new ORWCartDrawer(); });
} else {
  new ORWCartDrawer();
}
